import { useQueryParams } from '@marqvision/shared';
import { useSelectedBrandId } from '~/hooks';
import { useTab } from '~/pages/WebsiteContent/_impersonation/@hooks/useTab';
import {
  MarqBrandEnumsWebsiteBrandSortedColumnEnum,
  MarqCommonEnumsSortedOrderEnum,
  MarqCommonModelLocalDateRange,
  MarqFoundationEnumsCountryCodeEnum,
} from '~/swagger/data-contracts';
import { parseValueToValueArray } from '~/utils';
import { SORT_LIST } from '../../@constants';
import { FilterQueryParams, SortValue } from '../../@types';
import { parseTabToStatus } from '../../@utils';

export const useRequest = () => {
  const { brandId } = useSelectedBrandId();

  const { currentTab } = useTab();
  const filterParams = useQueryParams<FilterQueryParams>();

  const selectedCount = Object.entries(filterParams.params).reduce((prev, [key, value]) => {
    if (key.includes('search') || key.includes('sorted') || key.includes('scanned_status_filter')) {
      //filter 적용 갯수에 외부에 노출되어 있는 search, sort, scanned_status_filter 는 제외함
      return prev;
    }
    if (key.includes('date_end')) {
      //date 필터는 start, end 두 개로 나누어져 있어서 하나 제외함
      return prev;
    }
    if (Array.isArray(value)) {
      return prev + value.length;
    }
    return prev + Number(!!value);
  }, 0);
  const isResetVisible = selectedCount > 0;

  const {
    scanned_date_end,
    scanned_date_start,
    domain_id_filter,
    custom_tag_id_filter,
    country_filter,
    comment_user_type_filter,
    tp_progress_status_filter,
    tp_reported_filter,
    ...restParams
  } = filterParams.params;
  const domainIdFilter = !domain_id_filter || Array.isArray(domain_id_filter) ? domain_id_filter : [domain_id_filter];
  const scannedDateFilter: MarqCommonModelLocalDateRange | undefined =
    scanned_date_start && scanned_date_end
      ? {
          start: scanned_date_start,
          end: scanned_date_end,
        }
      : undefined;
  const customTagIdFilter =
    !custom_tag_id_filter || Array.isArray(custom_tag_id_filter) ? custom_tag_id_filter : [custom_tag_id_filter];
  const countryFilter = country_filter ? parseValueToValueArray(country_filter) : [];
  const commentUserTypeFilter = comment_user_type_filter ? parseValueToValueArray(comment_user_type_filter) : [];
  const testPurchaseProgressFilter = tp_progress_status_filter ? parseValueToValueArray(tp_progress_status_filter) : [];

  const handleFilterChange = <Key extends keyof FilterQueryParams>(key: Key, value: FilterQueryParams[Key]) => {
    if ((Array.isArray(value) && !value.length) || !value) {
      filterParams.removeParams(key);
    } else {
      filterParams.setParams({ [key]: value });
    }
  };

  const handleFilterReset = () => {
    const filterKeys = Object.keys(filterParams.params) as (keyof FilterQueryParams)[];
    filterParams.removeParams(filterKeys);
  };

  const handleSortChange = (value: SortValue) => {
    const { sortedColumn, sortedOrder } = getSort(value);

    filterParams.setParams({ sorted_column: sortedColumn, sorted_order: sortedOrder });
  };

  const handleOffsetChange = (offset: number) => {
    filterParams.setParams({ offset });
  };

  return {
    isResetVisible,
    selectedCount,
    request: {
      brand_id: brandId,
      ...restParams,
      ...parseTabToStatus(currentTab),
      offset: filterParams.params.offset || 0,
      search: filterParams.params.search ? decodeURIComponent(filterParams.params.search) : undefined,
      domain_id_filter: domainIdFilter,
      scanned_date_filter: scannedDateFilter,
      custom_tag_id_filter: customTagIdFilter || [],
      country_filter: countryFilter?.map((country) => country.toString() as MarqFoundationEnumsCountryCodeEnum),
      comment_user_type_filter: commentUserTypeFilter,
      tp_progress_status_filter: testPurchaseProgressFilter,
      tp_reported_filter: tp_reported_filter || undefined,
    },
    sort: {
      sorted_column: filterParams.params.sorted_column || getSort(SORT_LIST[currentTab]?.[0]).sortedColumn,
      sorted_order: filterParams.params.sorted_order || getSort(SORT_LIST[currentTab]?.[0]).sortedOrder,
    },
    onFilterChange: handleFilterChange,
    onSortChange: handleSortChange,
    onReset: handleFilterReset,
    onOffsetChange: handleOffsetChange,
  };
};

const getSort = (value?: SortValue) => {
  const [sortedColumn, sortedOrder] = (value ?? '').split('+') as [
    MarqBrandEnumsWebsiteBrandSortedColumnEnum,
    MarqCommonEnumsSortedOrderEnum
  ];

  return {
    sortedColumn,
    sortedOrder,
  };
};
